import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Logo from '../Logo';

import { ReactComponent as GatewaysIcon } from 'assets/images/icons/gateways.svg';
import { ReactComponent as LogoutIcon } from 'assets/images/icons/logout.svg';
import { ReactComponent as SubscriptionIcon } from 'assets/images/icons/subscription.svg';
import { ReactComponent as UserIcon } from 'assets/images/icons/user.svg';

import { ButtonSquare } from '../Button/ButtonSquare';

import { logout } from 'store/Auth/Auth.action';
import { showModal } from 'store/Modal/Modal.action';
import { IRootStore } from 'store/Root.reducer';
import { resetUserCreateData } from 'store/UserCreate/UserCreate.action';
import { resetUserUpdateData } from 'store/UserUpdate/UserUpdate.action';
import { IUserUpdateState } from 'store/UserUpdate/UserUpdate.type';

import { ModalType, Role } from 'entities/common';
import { isUserCreateFormFilled } from 'utils/helpers';
import { PAGES } from 'utils/link';

const Header: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userData, modalState, auth } = useSelector((state: IRootStore) => ({
    auth: state.auth,
    modalState: state.modal,
    userData: state[id ? 'userUpdate' : 'userCreate'],
  }));

  function handleUserCreateCheckModal(route: string, callback?: () => void) {
    if (modalState.active && modalState.modalType !== ModalType.PAGE_LEAVE) {
      return;
    }

    if (location.pathname.startsWith(PAGES.USER_CREATE)) {
      const isFormFilled = isUserCreateFormFilled(userData);

      if (isFormFilled) {
        dispatch(
          showModal({
            modalType: ModalType.PAGE_LEAVE,
            modalProps: {
              onConfirm: () => {
                dispatch(resetUserCreateData());

                if (callback) callback();
                navigate(route, { replace: true });
              },
            },
          }),
        );
      } else {
        if (callback) callback();
        navigate(route, { replace: true });
      }

      return;
    }

    const userUpdateBasePath = PAGES.USER_UPDATE.split(':id')[0];
    if (location.pathname.startsWith(userUpdateBasePath)) {
      const match = location.pathname.match(/\/user\/update\/([^/]+)/);
      const userIdFromPath = match?.[1];

      const isFormFilled = (userData as IUserUpdateState)?.isFormChanged;

      if (isFormFilled) {
        dispatch(
          showModal({
            modalType: ModalType.PAGE_LEAVE,
            modalProps: {
              onConfirm: () => {
                dispatch(resetUserUpdateData());

                if (userIdFromPath) {
                  localStorage.removeItem(`userUpdateData_${userIdFromPath}`);
                }

                if (callback) callback();
                navigate(route, { replace: true });
              },
            },
          }),
        );
      } else {
        if (callback) callback();
        localStorage.removeItem(`userUpdateData_${userIdFromPath}`);
        navigate(route, { replace: true });
      }
      return;
    }

    if (!!callback) callback();
    navigate(route);
  }

  const hasSensors =
    auth?.user?.role === Role.ADMIN || (auth?.user?.meta?.sensors_count ?? 0) > 0;

  const handleLogoClick = () => {
    if (!hasSensors) return;
    handleUserCreateCheckModal(
      auth?.user?.role === Role.USER
        ? PAGES.DASHBOARD.replace(':id', String(auth?.user?.id))
        : PAGES.ADMIN_USERS,
    );
  };

  const handleSubscriptionClick = () => {
    handleUserCreateCheckModal(PAGES.SUBSCRIPTION);
  };

  const handleUserClick = () => {
    handleUserCreateCheckModal(
      auth?.user?.role === Role.USER
        ? PAGES.DASHBOARD.replace(':id', String(auth?.user?.id))
        : PAGES.ADMIN_USERS,
    );
  };

  const handleGatewaysClick = () => {
    handleUserCreateCheckModal(PAGES.ADMIN_GATEWAYS);
  };

  const handleLogoutClick = () => {
    handleUserCreateCheckModal(PAGES.AUTH_LOGIN, () => dispatch(logout()));
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header__wrapper">
          <div className="logo" onClick={handleLogoClick} style={{ cursor: hasSensors ? 'pointer' : 'default' }}>
            <Logo />
          </div>

          <div className="header__menu">
            {hasSensors && auth?.user?.role === Role.USER && (
              <ButtonSquare onClick={handleSubscriptionClick}>
                <SubscriptionIcon />
              </ButtonSquare>
            )}

            {auth?.user?.role === Role.ADMIN && (
              <ButtonSquare onClick={handleGatewaysClick} aria-label="Gateways">
                <GatewaysIcon />
              </ButtonSquare>
            )}

            {hasSensors && (
              <ButtonSquare onClick={handleUserClick}>
                <UserIcon />
              </ButtonSquare>
            )}

            <ButtonSquare onClick={handleLogoutClick}>
              <LogoutIcon />
            </ButtonSquare>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
